import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useBrand } from '../context/BrandContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import useUsageLimits from '../hooks/useUsageLimits';
import UsageBadge from '../components/UsageBadge';
import PaywallModal from '../components/PaywallModal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Sparkles, 
  Loader2, 
  Download,
  Image as ImageIcon,
  Globe,
  Palette,
  Lock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const styleOptions = [
  { value: 'professional', label: 'Professional', description: 'Clean, corporate look' },
  { value: 'creative', label: 'Creative', description: 'Artistic and unique' },
  { value: 'minimalist', label: 'Minimalist', description: 'Simple and elegant' },
  { value: 'bold', label: 'Bold', description: 'Eye-catching and vibrant' },
  { value: 'tech', label: 'Tech/Futuristic', description: 'Modern tech aesthetic' },
];

export default function ImageGenerator() {
  const { currentBrand, brands } = useBrand();
  const [website, setWebsite] = useState(currentBrand.id);
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('professional');
  const [generatedImage, setGeneratedImage] = useState('');
  const [loading, setLoading] = useState(false);

  const { limits, refresh: refreshLimits, isLocked } = useUsageLimits();
  const [showPaywall, setShowPaywall] = useState(false);
  const locked = isLocked('image');

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter an image description');
      return;
    }
    if (locked) {
      setShowPaywall(true);
      toast.error('Free limit reached. Contact support to upgrade.');
      return;
    }

    setLoading(true);
    setGeneratedImage('');

    try {
      const response = await axios.post(
        `${API}/content/image`,
        {
          website,
          prompt: prompt.trim(),
          style
        },
        { withCredentials: true, timeout: 120000 }
      );
      setGeneratedImage(response.data.image_base64);
      toast.success('Image generated successfully!');
      refreshLimits();
    } catch (error) {
      console.error('Image generation error:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 403 && detail && typeof detail === 'object' && detail.code === 'FREE_TIER_LIMIT_REACHED') {
        setShowPaywall(true);
        toast.error('Free limit reached. Contact support to upgrade.');
        refreshLimits();
      } else {
        const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to generate image');
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${generatedImage}`;
    link.download = `${website.replace('.', '-')}-${Date.now()}.png`;
    link.click();
    toast.success('Image downloaded!');
  };

  const selectedBrand = brands[website];

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold text-zinc-100 font-['Manrope']">Image Generator</h1>
        <p className="text-zinc-400 mt-1">Create thumbnails and social media visuals with AI</p>
      </motion.div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-6"
        >
          <div className="p-6 rounded-xl bg-zinc-900/50 backdrop-blur-xl border border-zinc-800 space-y-6">
            {/* Website Selector */}
            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Website
              </Label>
              <Select value={website} onValueChange={setWebsite}>
                <SelectTrigger 
                  data-testid="image-website-select"
                  className="bg-zinc-900/50 border-zinc-800 h-12"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {Object.values(brands).map((brand) => (
                    <SelectItem key={brand.id} value={brand.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ background: brand.primary }}
                        />
                        {brand.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Style */}
            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Palette className="w-4 h-4" />
                Style
              </Label>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger 
                  data-testid="image-style-select"
                  className="bg-zinc-900/50 border-zinc-800 h-12"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {styleOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div>
                        <span>{opt.label}</span>
                        <span className="text-xs text-zinc-500 ml-2">- {opt.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Prompt */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Image Description *</Label>
              <Textarea
                data-testid="image-prompt-input"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g., A modern AI dashboard showing voice analytics with purple accents, professional tech aesthetic"
                className="bg-zinc-900/50 border-zinc-800 min-h-[150px] resize-none"
              />
              <p className="text-xs text-zinc-500">
                Be specific about colors, composition, and mood for best results
              </p>
            </div>

            {/* Generate Button */}
            <div className="space-y-2">
              <UsageBadge limits={limits} category="image" onUpgradeClick={() => setShowPaywall(true)} />
              <Button
                data-testid="generate-image-btn"
                onClick={handleGenerate}
                disabled={loading || !prompt.trim() || locked}
                title={locked ? 'Free limit reached. Upgrade to Pro to continue.' : ''}
                className="w-full h-12 rounded-xl font-medium transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                style={ locked
                  ? { background: 'linear-gradient(135deg, #52525b 0%, #3f3f46 100%)' }
                  : { background: `linear-gradient(135deg, ${selectedBrand?.primary || currentBrand.primary} 0%, ${selectedBrand?.secondary || currentBrand.secondary} 100%)` }
                }
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating (~60 sec)...
                  </span>
                ) : locked ? (
                  <span className="flex items-center gap-2">
                    <Lock className="w-5 h-5" />
                    Image limit reached — Upgrade
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Generate Image
                  </span>
                )}
              </Button>
            </div>
          </div>

          {/* Tips */}
          <div className="p-4 rounded-xl bg-zinc-900/30 border border-zinc-800">
            <h3 className="text-sm font-medium text-zinc-300 mb-2">Tips for Better Results</h3>
            <ul className="text-xs text-zinc-500 space-y-1">
              <li>• Include specific colors that match your brand</li>
              <li>• Describe the mood (professional, energetic, calm)</li>
              <li>• Mention composition (centered, diagonal, split)</li>
              <li>• Specify the use case (thumbnail, post, cover)</li>
            </ul>
          </div>
        </motion.div>

        {/* Output Section */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="p-6 rounded-xl bg-zinc-900/50 backdrop-blur-xl border border-zinc-800 h-full flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-zinc-100 font-['Manrope']">Generated Image</h2>
              {generatedImage && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDownload}
                  className="text-zinc-400 hover:text-zinc-200"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              )}
            </div>

            {loading ? (
              <div className="flex-1 flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <Loader2 
                    className="w-12 h-12 animate-spin mx-auto mb-4"
                    style={{ color: currentBrand.primary }}
                  />
                  <p className="text-zinc-400">Generating your image...</p>
                  <p className="text-sm text-zinc-500 mt-1">This may take up to 60 seconds</p>
                </div>
              </div>
            ) : generatedImage ? (
              <div className="flex-1 flex items-center justify-center">
                <img
                  src={`data:image/png;base64,${generatedImage}`}
                  alt="Generated"
                  className="max-w-full max-h-[500px] rounded-lg border border-zinc-800 shadow-2xl"
                  data-testid="generated-image"
                />
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center border-2 border-dashed border-zinc-800 rounded-xl min-h-[400px]">
                <div className="text-center p-8">
                  <ImageIcon className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-500">Your generated image will appear here</p>
                  <p className="text-sm text-zinc-600 mt-1">Fill in the form and click Generate</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      <PaywallModal
        open={showPaywall}
        onOpenChange={setShowPaywall}
        category="image"
        limit={limits?.image?.limit ?? 2}
      />
    </div>
  );
}
